import { PRODUCT_SORT_OPTIONS } from '@bulknutrients/bulk-shared-lib/constants';
import type { BulkAPIErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type { Category } from '@bulknutrients/bulk-shared-lib/interfaces/Category';
import type { MetaData } from '@bulknutrients/bulk-shared-lib/interfaces/MetaData';
import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import {
  getAllCategorySlugs,
  getCategoryBreadcrumbs,
  getCategoryData,
  getCategoryMetaData,
  getPreviewCategoryData,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/categoryData';
import {
  getAllProductsInCategory,
  getProductsInCategory,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/productData';
import { addBigCommerceDataToBaseProducts } from '@bulknutrients/bulk-shared-lib/lib/sanity/products/transform';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { Accordion } from '@components/Accordion';
import { CategoryAuthor } from '@components/Author/CategoryAuthor';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { Select } from '@components/FormComponents';
import {
  CheckboxRow,
  MultiSelect,
} from '@components/FormComponents/FormComponents';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { Icons } from '@components/Icons/Icons';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { LoadMore } from '@components/LoadMore/LoadMore';
import { getImageSrcUrl } from '@components/Media/Image/Image';
import {
  DarkBackgroundOverrides,
  PortableTextBlockRenderer,
} from '@components/PortableText/PortableText';
import { ProductCardVerticalVariant } from '@components/ProductCard/ProductCard';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { log } from '@lib/logs';
import { convertFAQToStructedData } from '@lib/structured';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import type { CategoryProductsResponse } from 'pages/api/category/products';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import useSWRInfinite from 'swr/infinite';

interface CategoryFacetOptions {
  productTypes: Array<{ value: string; label: string }>;
  diets: Array<{ value: string; label: string }>;
  flavours: Array<{ value: string; label: string }>;
}

type FilterFieldName = 'productTypeFilter' | 'dietFilter' | 'flavourFilter';

const BaseRendererOverride = {
  block: {
    h2: ({ children }: { children?: ReactNode }) => (
      <>
        <HorizontalDivider />
        <h2 className="italic [&+.hr]:hidden has-[+.hr]:mb-5">{children}</h2>
      </>
    ),
    h3: ({ children }: { children?: ReactNode }) => (
      <>
        <HorizontalDivider className="via-grey-mid" />
        <h3 className="font-bold">{children}</h3>
      </>
    ),
  },
};

const TopRendererOverride = {
  ...BaseRendererOverride,
  ...DarkBackgroundOverrides,
  block: {
    ...BaseRendererOverride.block,
    h1: ({ children }: { children?: ReactNode }) => {
      const normalizedChildren = Array.isArray(children)
        ? children.map((child) => {
            if (typeof child !== 'string') {
              return child;
            }

            return child.trim();
          })
        : typeof children === 'string'
          ? children.trim()
          : children;

      return (
        <h1 className="text-white text-[3rem] tiny:text-[3.3rem] sm:text-[3.75rem] not-italic font-bold uppercase leading-12.5">
          {normalizedChildren}
        </h1>
      );
    },
    normal: ({ children }: { children?: ReactNode }) => (
      <p className="text-white text-lg py-1">{children}</p>
    ),
  },
};

function MobileFilterTag({
  value,
  filterName,
  toggleFilterValue,
}: {
  value: string;
  filterName: FilterFieldName;
  toggleFilterValue: (filterName: FilterFieldName, value: string) => void;
}) {
  return (
    <button
      type="button"
      className="bg-grey-light rounded-full px-2 font-bold flex gap-2"
      onClick={() => toggleFilterValue(filterName, value)}
    >
      <span>{value}</span>
      <span>x</span>
    </button>
  );
}

function ProductList({
  categoryId,
  sortOption,
  serverProducts,
  facetOptions,
}: {
  categoryId: string;
  sortOption: string;
  serverProducts: BaseProduct[];
  facetOptions: CategoryFacetOptions;
}) {
  const PAGE_SIZE = 15;
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  // Watch the filters and sorting options
  const { watch, setValue } = useFormContext();
  const productTypeFilter = watch('productTypeFilter');
  const dietFilter = watch('dietFilter');
  const flavourFilter = watch('flavourFilter');

  const selectedProductTypes = Array.isArray(productTypeFilter)
    ? productTypeFilter
    : [];
  const selectedDiets = Array.isArray(dietFilter) ? dietFilter : [];
  const selectedFlavours = Array.isArray(flavourFilter) ? flavourFilter : [];

  const selectedProductTypesKey = selectedProductTypes.join('|');
  const selectedDietsKey = selectedDiets.join('|');
  const selectedFlavoursKey = selectedFlavours.join('|');
  const selectedValuesByField = {
    productTypeFilter: selectedProductTypes,
    dietFilter: selectedDiets,
    flavourFilter: selectedFlavours,
  } as const;
  const activeFilterCount =
    selectedProductTypes.length +
    selectedDiets.length +
    selectedFlavours.length;

  const clearAllFilters = () => {
    setValue('productTypeFilter', []);
    setValue('dietFilter', []);
    setValue('flavourFilter', []);
  };

  const toggleFilterValue = (
    fieldName: FilterFieldName,
    optionValue: string
  ) => {
    const currentValues = selectedValuesByField[fieldName];
    const nextValues = currentValues.includes(optionValue)
      ? currentValues.filter((value) => value !== optionValue)
      : [...currentValues, optionValue];

    setValue(fieldName, nextValues);
  };

  const renderMobileFilterCheckboxes = (
    fieldName: FilterFieldName,
    options: Array<{ value: string; label: string }>
  ) => (
    <div className="mt-2 flex flex-col gap-2 px-2 pb-2">
      {options.map((option) => {
        const isChecked = selectedValuesByField[fieldName].includes(
          option.value
        );

        return (
          <CheckboxRow
            key={option.value}
            id={`mobile-${fieldName}-${option.value}`}
            name={`mobile-${fieldName}-${option.value}`}
            label={option.label}
            checked={isChecked}
            onChange={() => toggleFilterValue(fieldName, option.value)}
            className={`gap-2 px-2 py-2 rounded-xl hover:bg-orange hover:text-white ${
              isChecked ? 'font-bold' : ''
            }`}
          />
        );
      })}
      {options.length === 0 && <span>No filters available</span>}
    </div>
  );

  const mobileFilterAccordionItems = [
    {
      title: <span className="text-base font-normal">Product Type</span>,
      content: renderMobileFilterCheckboxes(
        'productTypeFilter',
        facetOptions.productTypes
      ),
      id: 'mobile-filter-product-type',
    },
    {
      title: <span className="text-base font-normal">Diet</span>,
      content: renderMobileFilterCheckboxes('dietFilter', facetOptions.diets),
      id: 'mobile-filter-diet',
    },
    {
      title: <span className="text-base font-normal">Flavour</span>,
      content: renderMobileFilterCheckboxes(
        'flavourFilter',
        facetOptions.flavours
      ),
      id: 'mobile-filter-flavour',
    },
  ];

  // A function to get the SWR key of each page,
  // its return value will be accepted by `fetcher`.
  // If `null` is returned, the request of that page won't start.
  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.data.length) return null; // reached the end
    const apiUrl = new URL('/api/category/products', 'https://a');
    apiUrl.searchParams.set('pageIndex', pageIndex);
    apiUrl.searchParams.set('pageSize', PAGE_SIZE.toString());
    apiUrl.searchParams.set('category', categoryId);
    apiUrl.searchParams.set('sort', sortOption);
    apiUrl.searchParams.set('productType', selectedProductTypes.join(','));
    apiUrl.searchParams.set('diet', selectedDiets.join(','));
    apiUrl.searchParams.set('flavour', selectedFlavours.join(','));

    return apiUrl.pathname + apiUrl.search;
  };

  const fetcher = async (url) => {
    const res = await fetch(url);

    // If no error but no more data
    if (res.status === 204) {
      // empty data
      const error = new Error('No more products');
      throw error;
    }

    // If not ok, then get the error
    if (res.status !== 200) {
      const {
        error: { message },
      } = (await res.json()) as BulkAPIErrorResponse;
      const error = new Error('Unable to fetch blogs');
      error.message = message.join('. ');
      throw error;
    }

    // Everything as expected
    const { data: products } = await res.json();
    return products;
  };

  const { data, isLoading, size, setSize } =
    useSWRInfinite<CategoryProductsResponse>(getKey, fetcher, {
      keepPreviousData: true,
    });

  // Reset to the first page whenever the criteria changes, so that we don't end up in a weird state where we are on page 5 but the data only has 2 pages
  useEffect(() => {
    const criteriaKey = `${sortOption}|${selectedProductTypesKey}|${selectedDietsKey}|${selectedFlavoursKey}`;

    if (criteriaKey) {
      setSize(1);
    }
  }, [
    sortOption,
    selectedProductTypesKey,
    selectedDietsKey,
    selectedFlavoursKey,
    setSize,
  ]);

  const products: Array<BaseProduct> = data
    ? [...data.flatMap((d) => d.data)]
    : serverProducts;
  const removedCount: number = data
    ? data.reduce((acc, d) => acc + (d.removedCount ?? 0), 0)
    : 0;
  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data?.[0]?.data.length === 0;
  const countReturned = data?.[0].count ?? removedCount + 1;
  const isReachingEnd =
    isEmpty || products.length === countReturned - removedCount;

  return (
    <div className="full-bleed mx-auto my-4 max-w-[calc(100%-40px)] justify-self-center">
      {isEmpty ? (
        <div>Seems we don't have any products listed in that category</div>
      ) : (
        <>
          <form className="lg:min-w-[calc(1024px-40px)]">
            <div className="hidden lg:grid gap-4 grid-cols-4 *:w-full *:justify-self-stretch">
              <Select
                name="sortOption"
                label="Sort by"
                bordered={false}
                options={Object.entries(PRODUCT_SORT_OPTIONS).map(
                  ([, value]) => ({ value: value, label: value })
                )}
              />
              <MultiSelect
                name="productTypeFilter"
                label="Product Type"
                bordered={false}
                options={facetOptions.productTypes}
              />
              <MultiSelect
                name="dietFilter"
                label="Diet"
                bordered={false}
                options={facetOptions.diets}
              />
              <MultiSelect
                name="flavourFilter"
                label="Flavour"
                bordered={false}
                options={facetOptions.flavours}
              />
            </div>
            <div className="relative grid grid-cols-2 gap-4 lg:hidden mb-4">
              <Select
                name="sortOption"
                label="Sort by"
                bordered={false}
                className="lg:w-fit"
                options={Object.entries(PRODUCT_SORT_OPTIONS).map(
                  ([, value]) => ({ value: value, label: value })
                )}
              />
              <button
                type="button"
                className="font-inherit h-10 items-center cursor-pointer m-0 w-full max-w-[275px] text-left justify-between flex
                  appearance-none rounded-full bg-grey-light px-3 gap-3 leading-inherit text-black outline-none relative font-bold"
                onClick={() => setIsMobileFiltersOpen((open) => !open)}
                aria-expanded={isMobileFiltersOpen}
                aria-controls="mobile-filters-panel"
              >
                <span>
                  Filters
                  {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </span>
                <Icons.Chevron.Down
                  className={`h-4 w-4 transition-transform ${
                    isMobileFiltersOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isMobileFiltersOpen && (
                <div
                  id="mobile-filters-panel"
                  className="fixed inset-0 z-3 bg-white dark:bg-black overflow-y-auto"
                >
                  <div className="sticky top-0 z-1 flex items-center justify-between border-b border-grey-mid px-4 py-3 bg-inherit">
                    <h2 className="mb-0 text-lg font-bold">Filters</h2>
                    <button
                      type="button"
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-black hover:bg-black hover:text-white dark:border-white dark:hover:bg-white dark:hover:text-black"
                      onClick={() => setIsMobileFiltersOpen(false)}
                      aria-label="Close filters"
                    >
                      <Icons.Close size={20} />
                    </button>
                  </div>

                  <div className="px-4 py-4">
                    <div className="flex items-start justify-between pb-2">
                      <div className="flex gap-2 flex-wrap text-black">
                        {selectedProductTypes.map((p) => (
                          <MobileFilterTag
                            key={p}
                            value={p}
                            filterName="productTypeFilter"
                            toggleFilterValue={toggleFilterValue}
                          />
                        ))}
                        {selectedDiets.map((d) => (
                          <MobileFilterTag
                            key={d}
                            value={d}
                            filterName="dietFilter"
                            toggleFilterValue={toggleFilterValue}
                          />
                        ))}
                        {selectedFlavours.map((f) => (
                          <MobileFilterTag
                            key={f}
                            value={f}
                            filterName="flavourFilter"
                            toggleFilterValue={toggleFilterValue}
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        className="min-w-[70px] text-xs h-fit border border-black hover:bg-black hover:text-white dark:border-white dark:hover:bg-white dark:hover:text-black px-3 py-1 rounded-full disabled:opacity-50"
                        onClick={clearAllFilters}
                        disabled={activeFilterCount === 0}
                      >
                        Clear all
                      </button>
                    </div>
                    <Accordion
                      items={mobileFilterAccordionItems.map((i) => ({
                        ...i,
                        className: 'text-base font-bold',
                      }))}
                      defaultOpen={false}
                    />
                  </div>

                  <div className="sticky bottom-0 z-1 border-t border-grey-mid bg-inherit px-4 py-3 flex justify-center">
                    <Button
                      type="button"
                      onClick={() => setIsMobileFiltersOpen(false)}
                    >
                      View results ({products.length})
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>
          <div>
            <div
              data-products={products.length}
              className="mx-auto mb-4 grid w-fit grid-cols-1 tiny:grid-cols-2 gap-4 
              sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 
              lg:data-[products=3]:grid-cols-3 tiny:data-[products=2]:grid-cols-2 data-[products=1]:grid-cols-1"
            >
              {products.map((p, index) => (
                <ProductCardVerticalVariant
                  key={p.entityId}
                  product={p}
                  className={`gae-product-card-${index}`}
                  location="Category Products List"
                />
              ))}
            </div>

            <div className="mx-auto my-0 max-w-[480px] pt-8">
              <LoadMore
                clickHandler={() => setSize(size + 1)}
                disabled={(isLoadingMore || isReachingEnd) ?? false}
                text={
                  isLoadingMore
                    ? 'loading...'
                    : isReachingEnd
                      ? 'no more results'
                      : 'load more'
                }
                viewedResults={products.length ?? 0}
                totalResults={
                  data?.[0].count ? data?.[0].count - removedCount : 0
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface Props {
  categoryData: Category;
  serverProducts: BaseProduct[];
  facetOptions: CategoryFacetOptions;
  breadcrumbs: { title: string; path: string }[];
  faqStructuredData: unknown;
  meta: MetaData;
  isPreview: boolean;
}

export default function CategoryPage({
  categoryData,
  serverProducts,
  facetOptions,
  breadcrumbs,
  faqStructuredData,
  meta,
  isPreview,
}: Props): ReactElement {
  const { setRecentlyViewedPage } = useLocalStorage();

  useEffect(() => {
    setRecentlyViewedPage({
      page: { slug: categoryData.slug, title: categoryData.name },
      viewedTime: Date.now(),
    });
  }, [categoryData, setRecentlyViewedPage]);

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      sortOption: PRODUCT_SORT_OPTIONS.PRODUCTS,
      productTypeFilter: [],
      dietFilter: [],
      flavourFilter: [],
    },
  });

  const optionChanged =
    rhfMethods.watch('sortOption') ?? PRODUCT_SORT_OPTIONS.PRODUCTS;
  const desktopImage = categoryData.headerBannerImage?.desktopImage;
  const mobileImage = categoryData.headerBannerImage?.mobileImage;
  const desktopSrc = desktopImage
    ? getImageSrcUrl({ image: desktopImage, width: 2500 })
    : '';
  const mobileSrc = mobileImage
    ? getImageSrcUrl({ image: mobileImage, width: 768 })
    : desktopSrc;
  const hasBannerImage = !!(desktopSrc || mobileSrc);

  return (
    <>
      <Head>
        <title>{meta.metaTitle}</title>
        {(faqStructuredData as any) && (
          <script
            type="application/ld+json"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: we are inserting structured data
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqStructuredData),
            }}
          />
        )}
        <meta name="title" content={meta.metaTitle} />
        <meta name="description" content={meta.metaDescription} />
        {meta.noIndex && <meta name="robots" content="noindex" />}
        <link
          rel="canonical"
          href={
            meta.canonical
              ? meta.canonical
              : `${process.env.NEXT_PUBLIC_SERVER_URL}${categoryData.slug}`
          }
        />

        {/* Open Graph tags for Facebook, LinkedIn, etc. */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={meta.metaTitle} />
        <meta property="og:description" content={meta.metaDescription} />
        {meta.shareImage && (
          <>
            <meta
              property="og:image"
              content={getImageSrcUrl({
                image: meta.shareImage,
                width: 1200,
                height: 630,
              })}
            />
            <meta property="og:image:alt" content={meta.shareImage?.altText} />
          </>
        )}
        <meta
          property="og:url"
          content={
            meta.canonical
              ? meta.canonical
              : `${process.env.NEXT_PUBLIC_SERVER_URL}${categoryData.slug}`
          }
        />
      </Head>
      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <section className="full-bleed relative isolate overflow-hidden px-6 py-10 md:px-10 md:py-14">
          {hasBannerImage && (
            <>
              <div aria-hidden="true" className="absolute inset-0">
                <picture className="block h-full w-full">
                  {mobileSrc && (
                    <source media="(max-width: 767px)" srcSet={mobileSrc} />
                  )}
                  <img
                    src={desktopSrc || mobileSrc}
                    alt={desktopImage?.altText || mobileImage?.altText || ''}
                    className="h-full w-full object-cover"
                  />
                </picture>
              </div>
              <div className="absolute inset-0 bg-black/40" />
            </>
          )}
          <div
            className={`${hasBannerImage ? 'relative z-1 [&_p]:max-w-[640px] [&_p]:mx-auto [&_p]:text-lg text-center' : 'max-w-narrow-page mx-auto'}`}
          >
            <PortableTextBlockRenderer
              content={categoryData.description.top}
              componentsOverride={
                hasBannerImage ? TopRendererOverride : BaseRendererOverride
              }
            />
          </div>
        </section>
        <FormProvider {...rhfMethods}>
          <ProductList
            categoryId={categoryData._id}
            sortOption={optionChanged}
            serverProducts={serverProducts}
            facetOptions={facetOptions}
          />
        </FormProvider>
        <section id="bottom">
          {categoryData.author && (
            <CategoryAuthor author={categoryData.author} />
          )}
          {categoryData.description.bottom && (
            <PortableTextBlockRenderer
              content={categoryData.description.bottom}
              componentsOverride={BaseRendererOverride}
            />
          )}
        </section>
      </Container>
    </>
  );
}

CategoryPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getAllCategorySlugs();

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  try {
    let categoryData: Category;

    if (preview) {
      // This is preview mode
      categoryData = await getPreviewCategoryData(
        (previewData as { id: string }).id
      );
    } else {
      categoryData = await getCategoryData(
        params?.category?.toString() as string
      );
    }

    if (!categoryData) {
      // try redirect
      const redirect = await getRedirect(
        `/categories/${params?.category?.toString().split(',').join('/')}` as string
      );
      if (redirect) {
        return {
          redirect: {
            destination: redirect.newUrl,
            permanent: redirect.permanent,
          },
        };
      }

      // fallback failed
      return {
        notFound: true,
      };
    }

    if (categoryData.description.bottom) {
      // If there is a product carousel, add the BC data that is needed for a BaseProduct
      // Get all products out
      const products = [] as Array<any>;
      for (const content of categoryData.description.bottom) {
        if (
          content._type === 'carousel' &&
          content.items[0]._type === 'product'
        ) {
          products.push(...content.items);
        }
      }

      // Map products
      const mappedProducts = await addBigCommerceDataToBaseProducts(products);

      // Replace them
      categoryData.description.bottom = categoryData.description.bottom.map(
        (content) => {
          if (
            content._type === 'carousel' &&
            content.items[0]._type === 'product'
          ) {
            const mappedItems = content.items.map((item) =>
              mappedProducts.find((p) => p.entityId === item.entityId)
            );
            return {
              ...content,
              items: mappedItems,
            };
          }
          return content;
        }
      );
    }

    const breadcrumbs = await getCategoryBreadcrumbs(categoryData._id);
    const meta = await getCategoryMetaData(categoryData._id);
    const faqStructuredData = convertFAQToStructedData(categoryData.faqs);

    const serverProducts = (
      await getProductsInCategory(
        categoryData._id,
        0,
        15,
        PRODUCT_SORT_OPTIONS.PRODUCTS
      )
    ).data;

    const allCategoryProducts = await getAllProductsInCategory(
      categoryData._id
    );
    const productTypes = new Set<string>();
    const diets = new Set<string>();
    const flavours = new Set<string>();

    allCategoryProducts.forEach((p) => {
      p.tags.forEach((t) => {
        if (t.parent === 'Product Type') {
          productTypes.add(t.title);
        }
        if (t.parent === 'Diet') {
          diets.add(t.title);
        }
      });

      p.options.forEach((o) => {
        if (o.displayName === 'Flavour') {
          o.values.forEach((v) => {
            flavours.add(v.label);
          });
        }
      });
    });

    const facetOptions: CategoryFacetOptions = {
      productTypes: Array.from(productTypes)
        .sort((a, b) => a.localeCompare(b))
        .map((type) => ({ value: type, label: type })),
      diets: Array.from(diets)
        .sort((a, b) => a.localeCompare(b))
        .map((diet) => ({ value: diet, label: diet })),
      flavours: Array.from(flavours)
        .sort((a, b) => a.localeCompare(b))
        .map((flavour) => ({ value: flavour, label: flavour })),
    };

    return {
      props: {
        categoryData,
        serverProducts,
        facetOptions,
        breadcrumbs,
        faqStructuredData,
        meta,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    log({ error });
    throw error;
  }
};
